import {
  Button,
  Group,
  Input,
  NumberInput,
  Pill,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { UseFormReturnType, isEmail, useField } from '@mantine/form'
import { IconClock, IconMail } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PipelineFormValues } from './VlmPipelineForm'

type EmailActionFormProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
}

export const EmailActionForm = ({ form, index }: EmailActionFormProps) => {
  const intl = useIntl()

  const recipientField = useField({
    initialValue: '',
    validate: isEmail(
      intl.formatMessage({
        id: 'vlm.pipelines.create.actions.recipients.invalid'
      })
    )
  })

  const action = form.values.actions[index]

  if (action.type !== 'email') return null

  const recipients = action.recipients
  const recipientsError = form.errors[`actions.${index}.recipients`]

  const handleAddRecipient = async () => {
    const trimmed = recipientField.getValue().trim()

    if (!trimmed) return

    const error = await recipientField.validate()

    if (error) return

    const isDuplicate = recipients.some(
      (recipient) => recipient.toLowerCase() === trimmed.toLowerCase()
    )

    if (isDuplicate) return

    form.insertListItem(`actions.${index}.recipients`, trimmed)
    form.clearFieldError(`actions.${index}.recipients`)
    recipientField.reset()
  }

  const handleRemoveRecipient = (recipientIndex: number) => {
    form.removeListItem(`actions.${index}.recipients`, recipientIndex)
    form.clearFieldError(`actions.${index}.recipients`)
  }

  return (
    <Stack gap="xl" mt="md">
      <Stack gap="xs">
        <Text size="xs" fw={500} c="dimmed">
          <FormattedMessage id="vlm.pipelines.create.actions.recipients.label" />
        </Text>

        <Group gap="xs" wrap="nowrap" align="flex-start">
          <TextInput
            size="xs"
            type="email"
            style={{ flex: 1 }}
            placeholder={intl.formatMessage({
              id: 'vlm.pipelines.create.actions.recipients.placeholder'
            })}
            {...recipientField.getInputProps()}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              void handleAddRecipient()
            }}
          />

          <Button
            variant="light"
            size="xs"
            miw={100}
            onClick={() => void handleAddRecipient()}
          >
            <FormattedMessage id="vlm.pipelines.create.actions.recipients.addButton" />
          </Button>
        </Group>

        <Input.Wrapper error={recipientsError}>
          {recipients.length > 0 && (
            <Pill.Group mb={recipientsError ? 'xs' : 0}>
              {recipients.map((recipient, recipientIndex) => (
                <Pill
                  key={recipient}
                  size="md"
                  styles={{
                    root: {
                      outline: '1px solid var(--mantine-color-gray-4)'
                    }
                  }}
                  withRemoveButton
                  onRemove={() => handleRemoveRecipient(recipientIndex)}
                >
                  <Group gap={4} wrap="nowrap">
                    <IconMail size={16} color="var(--mantine-color-yellow-6)" />
                    <span>{recipient}</span>
                  </Group>
                </Pill>
              ))}
            </Pill.Group>
          )}
        </Input.Wrapper>
      </Stack>

      <Stack gap="xs">
        <Text size="xs" fw={500} c="dimmed">
          <FormattedMessage id="vlm.pipelines.create.actions.cooldown.label" />
        </Text>

        <NumberInput
          size="xs"
          w={220}
          min={1}
          leftSection={<IconClock size={14} />}
          description={intl.formatMessage({
            id: 'vlm.pipelines.create.actions.cooldown.description'
          })}
          {...form.getInputProps(`actions.${index}.cooldown_minutes`)}
        />
      </Stack>
    </Stack>
  )
}
